import type { Connection } from '@deskworks/shared-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Share2 } from 'lucide-react';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Field } from '@/components/field';
import { useAccountAdmin } from '@/components/secrets';
import { Section } from '@/components/section';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { useApiMutation, useWorkspace } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';

/** Where a connection is usable besides its own workspace, as for secrets: nowhere else, every workspace, or the chosen ones. */
type Sharing = 'none' | 'account' | 'selected';

const sharingOf = (c: Connection): Sharing => (c.sharedWith === 'all' ? 'account' : c.sharedWith.length ? 'selected' : 'none');

/**
 * Sharing a connection with other workspaces of the account (docs/features/connections.md, "Shared connections"):
 * their desks may use it too; it stays in its own workspace, which keeps its credential and changes it.
 */
export function ConnectionSharingSection({ connection: c, className }: { connection: Connection; className?: string }) {
  const accountAdmin = useAccountAdmin(c.workspaceId);
  const accountId = useWorkspace(c.workspaceId).data?.accountId;
  const workspaces = useQuery({
    queryKey: qk.accountWorkspaces(accountId ?? ''),
    queryFn: () => api.accounts.workspaces(accountId!),
    enabled: !!accountId && Array.isArray(c.sharedWith) && c.sharedWith.length > 0,
  });
  const shared = Array.isArray(c.sharedWith) ? c.sharedWith.map((id) => workspaces.data?.find((w) => w.id === id)?.name).filter(Boolean) : [];

  return (
    <Section
      icon={<Share2 />}
      title={t('Shared with')}
      description={t('Desks in other workspaces of the account may use this connection too. It stays here: only this workspace changes it.')}
      className={className}
      actions={
        accountAdmin && (
          <ShareConnectionDialog connection={c}>
            <Button variant="outline" size="sm">
              <Share2 /> {t('Share')}
            </Button>
          </ShareConnectionDialog>
        )
      }
    >
      <p className="text-sm text-muted-foreground">
        {c.sharedWith === 'all'
          ? t('All workspaces of the account')
          : c.sharedWith.length === 0
            ? accountAdmin
              ? t('Only this workspace.')
              : t('Only this workspace. Account admins can share it with others.')
            : shared.length
              ? shared.join(', ')
              : t('{n} workspaces', { n: c.sharedWith.length })}
      </p>
    </Section>
  );
}

function ShareConnectionDialog({ connection: c, children }: { connection: Connection; children: ReactNode }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [sharing, setSharing] = useState<Sharing>('none');
  const [chosen, setChosen] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    setSharing(sharingOf(c));
    setChosen(Array.isArray(c.sharedWith) ? c.sharedWith : []);
  }, [open, c]);

  const accountId = useWorkspace(c.workspaceId).data?.accountId;
  const workspaces = useQuery({
    queryKey: qk.accountWorkspaces(accountId ?? ''),
    queryFn: () => api.accounts.workspaces(accountId!),
    enabled: open && !!accountId,
  });
  const others = (workspaces.data ?? []).filter((w) => w.id !== c.workspaceId);
  // Ids of deleted workspaces drop out on the next save.
  const picked = chosen.filter((id) => !workspaces.data || others.some((w) => w.id === id));
  const sharedError = sharing === 'selected' && !picked.length ? t('Choose at least one workspace') : null;

  const save = useApiMutation(
    () => api.connections.update(c.id, { sharedWith: sharing === 'account' ? 'all' : sharing === 'selected' ? picked : [] }),
    {
      success: t('Sharing saved'),
      onSuccess: () => {
        // The connection shows in other workspaces' lists too.
        void queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'ws' && q.queryKey[2] === 'connections' });
        void queryClient.invalidateQueries({ queryKey: qk.connection(c.id) });
        setOpen(false);
      },
    },
  );

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!sharedError) save.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent>
        <form onSubmit={onSubmit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{t('Share “{name}”', { name: c.name })}</DialogTitle>
            <DialogDescription>
              {t('Desks there can add it and use it with the permission they give it. The credential and the sign-in stay here and are never shown there.')}
            </DialogDescription>
          </DialogHeader>
          <Field
            label={t('Also available in')}
            error={sharedError}
            hint={sharing === 'none' ? t('Only desks in this workspace can use it.') : t('Taking it back from a workspace works once no desk there uses it.')}
          >
            <Select value={sharing} onValueChange={(v) => setSharing(v as Sharing)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t('No other workspace')}</SelectItem>
                <SelectItem value="account">{t('All workspaces of the account')}</SelectItem>
                <SelectItem value="selected">{t('Selected workspaces')}</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {sharing === 'selected' && (
            <div className="-mt-2 grid max-h-48 gap-1.5 overflow-y-auto rounded-md border p-2">
              {workspaces.isLoading && <span className="text-xs text-muted-foreground">{t('Loading workspaces…')}</span>}
              {workspaces.isSuccess && others.length === 0 && <span className="text-xs text-muted-foreground">{t('The account has no other workspace yet.')}</span>}
              {others.map((w) => (
                <label key={w.id} className="flex cursor-pointer items-center gap-2 text-sm">
                  <Checkbox
                    checked={chosen.includes(w.id)}
                    onChange={(e) => setChosen((ids) => (e.target.checked ? [...ids, w.id] : ids.filter((id) => id !== w.id)))}
                  />
                  <span className="truncate">{w.name}</span>
                </label>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button type="submit" disabled={!!sharedError} loading={save.isPending}>
              {t('Save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
